import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, inject, model, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, ValidatorFn, Validators } from "@angular/forms";
import { Router } from "@angular/router";
import { ButtonModule } from "primeng/button";
import { DialogService, DynamicDialogRef } from "primeng/dynamicdialog";
import { InputTextModule } from "primeng/inputtext";
import { Select } from "primeng/select";
import { StepperModule } from 'primeng/stepper';
import { AuthService, OrganizationService, User, UserStateService } from "riterz-core";
import { AppService } from "../../services/app.service";
import { OrganizationSubmissionService } from "../../services/organization-submission.service";
import { debounceTime, distinctUntilChanged } from "rxjs";
import { DividerModule } from "primeng/divider";

@Component({
  selector: 'rt-organization-submit',
  imports: [
    ReactiveFormsModule,
    InputTextModule,
    ButtonModule,
    Select,
    StepperModule,
    DividerModule,
  ],
  standalone: true,
  templateUrl: './organization-submit.component.html',
  providers: [DialogService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrganizationSubmitComponent implements OnInit {
  private appService = inject(AppService);
  private dialogRef = inject(DynamicDialogRef);
  private router = inject(Router);
  private authService = inject(AuthService);
  private userStateService = inject(UserStateService);
  private organizationService = inject(OrganizationService);
  private submissionService = inject(OrganizationSubmissionService);
  private destroyRef = inject(DestroyRef);

  readonly nameExists = signal<boolean>(false);
  readonly websiteExists = signal<boolean>(false);
  readonly currentStep = model<number>(1);
  readonly currentUser = signal<User|null>(null);
  readonly emailMatch = signal<'checking'|'matches'|'mismatches'>('checking');

  readonly submitForm: FormGroup = new FormGroup({
    name: new FormControl('', [Validators.required]),
    type: new FormControl('network', [Validators.required]),
    website_url: new FormControl('', [Validators.required, Validators.pattern(/https?:\/\/.+/)]),
    facebook_url: new FormControl('', [Validators.pattern(/https?:\/\/(www\.)?facebook\.com\/.+/)]),
    instagram_url: new FormControl('', [Validators.pattern(/https?:\/\/(www\.)?instagram\.com\/.+/)]),
  }, this.atLeastOneHasValue(['facebook_url', 'instagram_url']));

  readonly organizationTypes = [
    { id: 'rights-holder', name: 'Rights holder' },
    { id: 'brand', name: 'Brand' },
  ];

  readonly submitting = signal<boolean>(false);
  readonly validating = signal<boolean>(false);

  ngOnInit(): void {
    // Read the shared subject — AppComponent already loaded the user with its relations.
    this.userStateService
      .currentUser
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((user) => {
        this.currentUser.set(user);
      });

    this.submitForm
      .get('name')?.valueChanges
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.checkName();
      });

    this.submitForm
      .get('website_url')?.valueChanges
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.checkWebsite();
      });
  }

  submit(): void {
    if (this.submitForm.invalid) { return; }

    const params = this.submitForm.value;

    this.submitting.set(true);
    this.organizationService
      .submitOrganization(params)
      .subscribe({
        next: () => {
          this.submitting.set(false);
          // Membership isn't granted until an admin approves, so record the
          // attempt locally — it's the only way /onboarding can show a pending
          // state instead of asking the user to submit all over again.
          this.submissionService.record(this.currentUser()?.id ?? '', {
            name: params.name,
            type: params.type,
            kind: 'submit',
            submitted_at: new Date().toISOString(),
          });
          this.currentStep.set(4);
        },
        error: (error) => {
          this.appService.showToast({ 
            severity: 'error', 
            summary: `Error`, 
            detail: error.message 
          });
          this.submitting.set(false);
        }
      });
  }

  checkName(): void {
    const name = this.submitForm.get('name')?.value;

    if (name === '') { 
      this.nameExists.set(false);  
      return; 
    }

    this.validating.set(true);
    this.organizationService
      .validateOrganizationName({ name })
      .subscribe({
        next: (response) => {
          this.nameExists.set(!(response as { valid: boolean }).valid);
          this.validating.set(false);
        },
        error: (error) => {
          this.appService.showToast({ 
            severity: 'error', 
            summary: `Error`, 
            detail: error.message 
          });
          this.validating.set(false);
        }
      });
  }

  checkWebsite(): void {
    const url = this.submitForm.get('website_url')?.value;

    if (url === '') { 
      this.websiteExists.set(false);  
      return; 
    }

    this.validating.set(true);
    this.organizationService
      .validateOrganizationWebsite({ url })
      .subscribe({
        next: (response) => {
          this.websiteExists.set(!(response as { valid: boolean }).valid);
          this.validating.set(false);
        },
        error: (error) => {
          this.appService.showToast({ 
            severity: 'error', 
            summary: `Error`, 
            detail: error.message 
          });
          this.validating.set(false);
        }
      });
  }

  checkEmailMatch(): void {
    if (!this.currentUser) { return; }
    if (this.submitForm.get('website_url')?.value === '') { return; }

    this.emailMatch.set('checking');

    const email = this.currentUser()?.email;
    const emailDomain = email?.split('@')[1];
    const websiteUrl = this.submitForm.get('website_url')?.value;

    // console.log(`Checking email match ${emailDomain}, ${websiteUrl}`);
    setTimeout(() => {
      if (websiteUrl.includes(emailDomain)) {
        this.emailMatch.set('matches');
        this.currentStep.set(3);
      } else {
        this.emailMatch.set('mismatches');
      }
    }, 1500);
  }

  logout(): void {
    this.authService
      .logout()
      .subscribe(() => {
        this.router.navigate(['/']);
        this.dialogRef.close();
      });
  }

  close(): void {
    this.dialogRef.close();
  }

  atLeastOneHasValue(fields: string[]): ValidatorFn {
    return (control: AbstractControl) => {
      const group = control as FormGroup;
      for (const fieldName of fields) {
        if (group.get(fieldName)?.value) {
          return null;
        }
      }
      return { socialRequired: true };
    };
  };
}
